import styled from "styled-components";
import moment from "moment-timezone";
import { AirportLegInfo } from "@/components/molecules";
import { Col, Row, Divider, Popover } from "antd";
import { RAButtonGhost, RAImage, RATypography } from "@/components/customs";
import { lightTheme } from "@/themes/ThemeConfig";
import { SvgAirplaneSeat, SvgArrowStraightRight } from "@/assets/icons";
import { formatCurrency } from "@/utils";
import { RAEmptyLegLabel } from "@/components/atoms";
import { useState } from "react";
import { useSelector } from "react-redux";
import { selectFormatDateTimeDeparture } from "@/stores/slices/authSlice";

export function CardEmptyLegDetail({ detail = {}, onShowDetails }) {
	const {
		aircraft = {},
		departure,
		departure_window,
		from_airport,
		label = "",
		to_airport,
		price_inc_tax,
		empty_leg,
	} = detail;
	const formatDTDeparture = useSelector(selectFormatDateTimeDeparture);

	const _renderDeparture = () => {
		if (!departure?.utc_datetime && label === "INVITED" && empty_leg?.departure?.utc_datetime) {
			return (
				<>
					<RATypography variant="body4" color="#595959">
						{empty_leg?.departure_window?.utc_datetime ? "Departure window" : "Departure time"}
					</RATypography>
					{empty_leg?.departure_window?.utc_datetime ? (
						<>
							<Row align="middle" gutter={[20, 12]} style={{ marginTop: "4px" }}>
								<Col span={5}>
									<RATypography variant="body4" color="#595959">
										From
									</RATypography>
								</Col>
								<Col span={19}>
									<RATypography variant="sub-heading4" color={lightTheme.color_text_500}>
										{empty_leg?.departure?.utc_datetime
											? moment(empty_leg?.departure?.utc_datetime)
													.tz(from_airport?.location?.timezone)
													.format("DD MMM YYYY")
											: "TBD"}
									</RATypography>
								</Col>
							</Row>
							<Row align="middle" gutter={[20, 12]} style={{ marginTop: "4px" }}>
								<Col span={5}>
									<RATypography variant="body4" color="#595959">
										To
									</RATypography>
								</Col>
								<Col span={19}>
									<RATypography variant="sub-heading4" color={lightTheme.color_text_500}>
										{empty_leg?.departure_window?.utc_datetime
											? moment(empty_leg?.departure_window?.utc_datetime)
													.tz(from_airport?.location?.timezone)
													.format("DD MMM YYYY")
											: "TBD"}
									</RATypography>
								</Col>
							</Row>
						</>
					) : (
						<RATypography variant="sub-heading4" color={lightTheme.color_text_500} top="4px">
							{`${moment(empty_leg?.departure?.utc_datetime)
								.tz(from_airport?.location?.timezone)
								.format("DD MMM YYYY")}`}
						</RATypography>
					)}

					<RATypography variant="label4" color={lightTheme.color_primary_500} top="4px">
						INQUIRE FOR DEPARTURE TIME
					</RATypography>
				</>
			);
		}

		if (departure_window) {
			return (
				<>
					<RATypography variant="body4" color="#595959">
						Departure window
					</RATypography>
					<Row align="middle" gutter={[20, 12]} style={{ marginTop: "4px" }}>
						<Col span={5}>
							<RATypography variant="body4" color="#595959">
								From
							</RATypography>
						</Col>
						<Col span={19}>
							<RATypography variant="sub-heading4" color={lightTheme.color_text_500}>
								{departure?.utc_datetime
									? moment(departure?.utc_datetime)
											.tz(from_airport?.location?.timezone)
											.format(formatDTDeparture)
									: "TBD"}
							</RATypography>
						</Col>
					</Row>
					<Row align="middle" gutter={[20, 12]} style={{ marginTop: "4px" }}>
						<Col span={5}>
							<RATypography variant="body4" color="#595959">
								To
							</RATypography>
						</Col>
						<Col span={19}>
							<RATypography variant="sub-heading4" color={lightTheme.color_text_500}>
								{departure_window?.utc_datetime
									? moment(departure_window?.utc_datetime)
											.tz(from_airport?.location?.timezone)
											.format(formatDTDeparture)
									: "TBD"}
							</RATypography>
						</Col>
					</Row>
				</>
			);
		}

		return (
			<>
				<RATypography variant="body4" color="#595959">
					Departure time
				</RATypography>
				<RATypography variant="sub-heading4" color={lightTheme.color_text_500} top="4px">
					{departure?.utc_datetime
						? moment(departure?.utc_datetime).tz(from_airport?.location?.timezone).format(formatDTDeparture)
						: "TBD"}
				</RATypography>
			</>
		);
	};

	return (
		<ContainerCharter data-testid="emptyLegs_row">
			<Row gutter={[20, 20]}>
				<Col xl={7} lg={10} md={10} sm={24} xs={24}>
					<WrapperAircraftImage>
						<RAImage
							url={
								aircraft?.images && aircraft?.images[0]
									? aircraft?.images[0].url
									: "/images/example/aircraft_1.png"
							}
							width="100%"
							height="136px"
							borderRadius="6px"
						/>
						<WrapperLabel>
							<RAEmptyLegLabel label={label} />
						</WrapperLabel>
					</WrapperAircraftImage>
				</Col>
				<Col xl={17} lg={14} md={14} sm={24} xs={24}>
					<WrapperLegItem>
						<Row gutter={[20, 12]} style={{ width: "100%" }} justify="space-between" align="middle">
							<Col>
								<RATypography variant="sub-heading2" color="#222222">
									{aircraft?.name ?? "Unknown"}
								</RATypography>
							</Col>
							<Col>
								<div style={{ display: "flex" }}>
									<RAButtonGhost
										onClick={() => onShowDetails(detail)}
										title="DETAILS"
										suffix={<SvgArrowStraightRight />}
										data-testid="emptyLegs_detailsBtn"
									/>
								</div>
							</Col>
						</Row>
						<Divider />
						<Row gutter={[20, 12]} style={{ width: "100%" }}>
							<Col xl={15} lg={24} md={24} sm={24} xs={24}>
								<AirportLegInfo from_airport={from_airport} to_airport={to_airport} />
							</Col>
							<Col xl={9} lg={24} md={24} sm={24} xs={24}>
								{_renderDeparture()}
							</Col>
						</Row>
					</WrapperLegItem>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 20]} justify="space-between" align="middle" style={{ flexWrap: "wrap" }}>
				<Col xl={16} lg={14} md={12} sm={24} xs={24}>
					<WrapperAmenities>
						<Row>
							<SvgAirplaneSeat />
							<RATypography variant="sub-heading4" color="#65686F" left={12} right={12}>
								{`${aircraft?.max_pax ?? "_"} Pax`}
							</RATypography>
						</Row>
						<CardAmenities amenities={aircraft?.amenities} />
					</WrapperAmenities>
				</Col>
				<Col xl={8} lg={10} md={12} sm={24} xs={24}>
					<Row gutter={[20, 12]} justify="end" align="middle">
						<Col>
							<RATypography variant="label3" color="#A8A8A8">
								PRICE
							</RATypography>
						</Col>
						<Col>
							<RATypography variant="label1" color="#222222">
								{!price_inc_tax ? "REQUEST PRICING" : formatCurrency(price_inc_tax, "USD", 0)}
							</RATypography>
						</Col>
					</Row>
				</Col>
			</Row>
		</ContainerCharter>
	);
}

const CardAmenities = ({ amenities = [] }) => {
	const [showAll, setShowAll] = useState(false);

	if (!amenities || amenities?.length === 0) return null;
	return amenities?.map((item, index) => {
		if (index < 5 || showAll) {
			return (
				<Popover
					key={index}
					placement="bottom"
					title=""
					content={() => {
						return (
							<RATypography variant="body4" color="#595959">
								{item?.name || ""}
							</RATypography>
						);
					}}
				>
					<AmenitiesItem>
						<RAImage url={item?.image?.url} width="20px" height="20px" />
					</AmenitiesItem>
				</Popover>
			);
		}
		if (index === 5 && !showAll) {
			return (
				<AmenitiesItem key={index}>
					<WrapperShowMore onClick={() => setShowAll(true)}>
						<RATypography variant="sub-heading4" color="#A8A8A8">
							{`+${amenities?.length - 5}`}
						</RATypography>
					</WrapperShowMore>
				</AmenitiesItem>
			);
		}
	});
};

const ContainerCharter = styled.div`
	width: "100%";
	padding: 20px 16px;
	gap: 30px;
	min-height: 230px;
	border: 1px solid #e9e8e8;
	border-radius: 12px;
`;
const WrapperLegItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	.ant-divider-horizontal {
		margin: 10px 0;
	}
`;
const WrapperAmenities = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
`;
const AmenitiesItem = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0 12px;
	border-left: 1px solid #e9e8e8;
`;
const WrapperAircraftImage = styled.div`
	position: relative;
`;
const WrapperLabel = styled.div`
	position: absolute;
	top: 0px;
	left: 0px;
`;
const WrapperShowMore = styled.div`
	border-radius: 40px;
	background: #f3f2f2;
	display: flex;
	padding: 3px 10px;
	align-items: center;
	gap: 8px;
	cursor: pointer;
`;
